import React, { useState } from "react";
import {
    Box,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    CircularProgress,
    TableSortLabel,
    Button,
    Paper,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    Select,
    Container,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import * as Sentry from "@sentry/react";
import API_ENDPOINTS from "../../apis/endpoints";
import useSWR, { mutate } from "swr";
import AddUserDialog from "../../components/addUserDialog/AddUserDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import SearchInput from "../../components/SearchInput";
import useAuth from "../../context";

const UserList = () => {
    const auth = useAuth();
    const [searchTerm, setSearchTerm] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortColumn, setSortColumn] = useState("first_name");
    const [sortDirection, setSortDirection] = useState("asc");
    const [actionDialogOpen, setActionDialogOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [actionType, setActionType] = useState("");
    const [reason, setReason] = useState("");
    const [newRole, setNewRole] = useState("");
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState("This is an info message");
    const [toastDescription, setToastDescription] = useState("");
    const [toastSeverity, setToastSeverity] = useState("success");
    const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const handleCloseToast = () => {
        setToastOpen(false);
    };

    const queryParam = searchQuery ? `&query=${searchQuery}` : "";
    const sortParam = `&sort=${
        sortDirection === "desc" ? `-${sortColumn}` : sortColumn
    }`;

    // Fetch users data
    const { data, isLoading, mutate } = useSWR(
        `${API_ENDPOINTS.ADMIN_USER}?limit=${rowsPerPage}&offset=${
            page * rowsPerPage
        }${queryParam}${sortParam}`
    );

    const users = data?.results ?? [];
    const totalUsers = data?.meta?.total ?? 0;
    const summary = data?.summary ?? {};

    const handleAddUserClick = () => {
        setAddUserDialogOpen(true);
    };

    const handleCloseAddUserDialog = () => {
        setAddUserDialogOpen(false);
        mutate();
    };

    const handleActionClick = (event, userId) => {
        setAnchorEl(event.currentTarget);
        setSelectedUserId(userId);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleActionSelect = (action) => {
        setActionType(action);
        setActionDialogOpen(true);
        handleCloseMenu();
    };

    const handleConfirmAction = async () => {
        const storedDeviceId = localStorage.getItem("device-id");

        try {
            const formData = new FormData();

            let url = `${API_ENDPOINTS.ADMIN_USER}${selectedUserId}/`;
            let method = "PATCH";

            if (actionType === "block" || actionType === "unblock") {
                formData.append(
                    actionType === "block" ? "reason" : "comment",
                    reason
                );
                formData.append("type", actionType);
            } else if (actionType === "changeRole") {
                formData.append("role", newRole);
                formData.append("type", "role");
            }

            const response = await fetch(url, {
                method,
                headers: {
                    Authorization: `Bearer ${auth.accessToken}`,
                    "x-cc-device-id": storedDeviceId,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            mutate(
                `${API_ENDPOINTS.ADMIN_USER}?limit=${rowsPerPage}&offset=${
                    page * rowsPerPage
                }${queryParam}${sortParam}`
            );

            setToastMessage(
                actionType === "block"
                    ? "User blocked successfully"
                    : actionType === "unblock"
                      ? "User unblocked successfully"
                      : "User role changed successfully"
            );
            setToastSeverity("success");
        } catch (error) {
            setToastMessage(error.message);
            setToastDescription(error.description);
            setToastSeverity("error");
            console.log(error);
        } finally {
            setToastOpen(true);
            setActionDialogOpen(false);
            setReason("");
            setNewRole("");
        }
    };

    const handleSearch = () => {
        setSearchQuery(searchTerm);
        setPage(0);
        Sentry.addBreadcrumb({
            category: "user",
            message: `User searched for: ${searchTerm}`,
            level: "info",
        });
    };

    const handleSortRequest = (column) => {
        const isAsc = sortColumn === column && sortDirection === "asc";
        setSortDirection(isAsc ? "desc" : "asc");
        setSortColumn(column);
        Sentry.addBreadcrumb({
            category: "user",
            message: `Sorting by ${column} in ${isAsc ? "desc" : "asc"} order`,
            level: "info",
        });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleKeyPress = (event) => {
        if (event.key === "Enter") {
            handleSearch();
        }
    };

    return (
        <Container maxWidth="lg">
            {" "}
            <Box className="user-list-container">
                <Typography variant="featureAccent">User List</Typography>

                <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    mb={2}
                    mt={3}
                >
                    <Box>
                        <SearchInput
                            onSearch={handleSearch}
                            placeHolder={"User..."}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            sx={{ width: "65.5vw" }}
                        />
                    </Box>
                    <Button
                        variant="contained"
                        onClick={handleAddUserClick}
                        size="large"
                        sx={{ marginLeft: "20px" }}
                    >
                        <PersonAddAltIcon
                            sx={{ marginRight: "10px", marginLeft: "-10px" }}
                        />{" "}
                        Add User
                    </Button>
                </Box>

                {isLoading ? (
                    <Box className="loader-container">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Paper className="table-paper">
                        <TableContainer className="table-container">
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {summary?.sortable_columns?.map(
                                            (column) => (
                                                <TableCell
                                                    key={column}
                                                    className="table-header-cell"
                                                >
                                                    <TableSortLabel
                                                        active={
                                                            sortColumn ===
                                                            column
                                                        }
                                                        direction={
                                                            sortColumn ===
                                                            column
                                                                ? sortDirection
                                                                : "asc"
                                                        }
                                                        onClick={() =>
                                                            handleSortRequest(
                                                                column
                                                            )
                                                        }
                                                    >
                                                        {column
                                                            .split("_")
                                                            .map(
                                                                (word) =>
                                                                    word
                                                                        .charAt(
                                                                            0
                                                                        )
                                                                        .toUpperCase() +
                                                                    word.slice(
                                                                        1
                                                                    )
                                                            )
                                                            .join(" ")}
                                                    </TableSortLabel>
                                                </TableCell>
                                            )
                                        )}
                                        <TableCell className="table-header-cell">
                                            Action
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.length > 0 ? (
                                        users.map((user) => (
                                            <TableRow
                                                key={user.id}
                                                className="table-row"
                                            >
                                                {summary.sortable_columns.map(
                                                    (column) => (
                                                        <TableCell
                                                            key={column}
                                                            className="table-cell"
                                                        >
                                                            {formatCellValue(
                                                                user[column]
                                                            )}
                                                        </TableCell>
                                                    )
                                                )}
                                                <TableCell className="table-cell">
                                                    <IconButton
                                                        onClick={(event) =>
                                                            handleActionClick(
                                                                event,
                                                                user.id
                                                            )
                                                        }
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell
                                                colSpan={
                                                    summary?.sortable_columns
                                                        ?.length + 1
                                                }
                                                align="center"
                                            >
                                                No users found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            count={totalUsers}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Paper>
                )}

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    sx={{ marginLeft: "-2%" }}
                >
                    <MenuItem
                        onClick={() =>
                            handleActionSelect(
                                !users.find((u) => u.id === selectedUserId)
                                    ?.profile_active
                                    ? "unblock"
                                    : "block"
                            )
                        }
                    >
                        {!users.find((u) => u.id === selectedUserId)
                            ?.profile_active
                            ? "Unblock"
                            : "Block"}
                    </MenuItem>
                    <MenuItem onClick={() => handleActionSelect("changeRole")}>
                        Change Role
                    </MenuItem>
                </Menu>

                <Dialog
                    open={actionDialogOpen}
                    onClose={() => setActionDialogOpen(false)}
                >
                    <DialogTitle>
                        {actionType === "block"
                            ? "Block User"
                            : actionType === "unblock"
                              ? "Unblock User"
                              : "Change User Role"}
                    </DialogTitle>
                    <DialogContent>
                        {actionType === "block" || actionType === "unblock" ? (
                            <>
                                <DialogContentText>
                                    {actionType === "block"
                                        ? "are you sure you want to block this user "
                                        : "are you sure you want to unblock this user"}{" "}
                                </DialogContentText>
                            </>
                        ) : (
                            <>
                                <DialogContentText>
                                    Please select the new role for this user.
                                </DialogContentText>
                                <Select
                                    fullWidth
                                    value={newRole}
                                    onChange={(e) => setNewRole(e.target.value)}
                                    variant="standard"
                                >
                                    <MenuItem value="admin">Admin</MenuItem>
                                    <MenuItem value="agent">Agent</MenuItem>
                                    <MenuItem value="talent">Talent</MenuItem>
                                    <MenuItem value="executive_admin">
                                        Executive Admin
                                    </MenuItem>
                                </Select>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setActionDialogOpen(false)}
                            color="primary"
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmAction} color="primary">
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

                <AddUserDialog
                    open={addUserDialogOpen}
                    onClose={handleCloseAddUserDialog}
                />

                <CustomSnackbar
                    open={toastOpen}
                    handleClose={handleCloseToast}
                    message={toastMessage}
                    severity={toastSeverity}
                    description={toastDescription}
                />
            </Box>
        </Container>
    );
};

export default UserList;

function formatCellValue(val) {
    if (!val) {
        return "-";
    }

    if (typeof val === "boolean") {
        return val ? "Yes" : "No";
    }

    if (new Date(val).getTime()) {
        return new Date(val).toLocaleString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    }

    return val;
}
